import { useState } from 'react';
import type { Dir } from '../wire';
import { hapticPulse } from './haptics';

export interface DPadProps {
  onDir: (dir: Dir) => void;
  disabled?: boolean;
}

/** Pinned-bottom directional pad. SPEC.md §5: touch targets >= 44x44 CSS px,
 * press-and-hold holds the direction (the engine itself keeps a Pac-Man
 * moving in its last direction until a wall/new turn -- see
 * packages/engine/src/pacman.ts -- so ONE input message per press is
 * sufficient; "release stops sending" just means we don't keep re-sending
 * while held). Buttons are real <button>s so they are keyboard-operable
 * (Enter/Space) and screen-reader labeled, not just a touch decoration.
 * Each press also fires a short haptic pulse (gated by the Settings
 * haptics toggle + feature detection, see game/haptics.ts) -- never for a
 * keyboard-synthesized activation only, real presses get it too since both
 * paths call the same `press()`. */
export function DPad({ onDir, disabled }: DPadProps): JSX.Element {
  const [active, setActive] = useState<Dir | null>(null);

  function press(dir: Dir): void {
    if (disabled) return;
    setActive(dir);
    hapticPulse();
    onDir(dir);
  }
  function release(): void {
    setActive(null);
  }

  // Pointer events (mouse/touch/pen) drive press() on pointerdown for
  // lowest latency. A native <button> click ALSO fires from Enter/Space
  // (keyboard activation), which is the only path a keyboard/AT user has --
  // e.detail === 0 reliably distinguishes a keyboard-synthesized click from
  // a real pointer click (which already handled the press via pointerdown),
  // so this fires exactly once per input regardless of device.
  function dirProps(dir: Dir, label: string, rotate: number) {
    return {
      type: 'button' as const,
      className: `dpad-btn dpad-${dir}${active === dir ? ' dpad-active' : ''}`,
      'aria-label': label,
      disabled,
      onPointerDown: () => press(dir),
      onPointerUp: release,
      onPointerLeave: release,
      onPointerCancel: release,
      onClick: (e: React.MouseEvent) => {
        if (e.detail === 0) press(dir);
      },
      children: <ArrowGlyph rotate={rotate} />,
    };
  }

  return (
    <div className="dpad" data-testid="dpad" aria-label="Directional pad">
      <button {...dirProps('up', 'Move up', 0)} />
      <button {...dirProps('left', 'Move left', -90)} />
      <button type="button" className="dpad-btn dpad-center" aria-hidden="true" tabIndex={-1} disabled />
      <button {...dirProps('right', 'Move right', 90)} />
      <button {...dirProps('down', 'Move down', 180)} />
    </div>
  );
}

function ArrowGlyph({ rotate }: { rotate: number }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.4em"
      height="1.4em"
      style={{ transform: `rotate(${rotate}deg)` }}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 3 L20 15 L14 15 L14 21 L10 21 L10 15 L4 15 Z" fill="currentColor" />
    </svg>
  );
}
